/**
 * "You have unsaved changes" for the whole app. A page with unsaved edits sets a
 * message; any in-app link click (sidebar, client nav, user menu), the browser's
 * Back and Forward (buttons, trackpad swipes, the phone's back gesture) and any
 * programmatic navigation that calls confirmLeave() then ask first.
 *
 * BrowserRouter has no navigation blocker, so link clicks and popstate are
 * caught here in the capture phase, before React Router's own handlers run.
 */
let message: string | null = null;
/** React Router's index of the history entry the guarded page is on. */
let guardIdx: number | null = null;
/** The next popstate is our own step back to the guarded entry: swallow it. */
let restoring = false;

/** React Router keeps the entry's position in history.state.idx. */
export function historyIndex(state: unknown): number | null {
  const i = (state as { idx?: unknown } | null)?.idx;
  return typeof i === "number" ? i : null;
}

/** How far to move to get back to the guarded entry after a refused Back or
 *  Forward (+1 after a Back, -1 after a Forward). Unknown positions: assume Back. */
export function stepBack(guarded: number | null, landedOn: number | null): number {
  return guarded != null && landedOn != null ? guarded - landedOn : 1;
}

export function setLeaveGuard(next: string | null) {
  message = next;
  guardIdx = next && typeof window !== "undefined" ? historyIndex(window.history.state) : null;
}

/** True when it is fine to leave: nothing unsaved, or the person agreed. */
export function confirmLeave(): boolean {
  if (!message) return true;
  if (!window.confirm(message)) return false;
  message = null;
  return true;
}

if (typeof document !== "undefined") {
  document.addEventListener(
    "click",
    (e) => {
      if (!message || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link || link.target === "_blank" || link.hasAttribute("download")) return;
      const to = new URL(link.href, window.location.href);
      if (to.origin !== window.location.origin) return;
      if (to.pathname === window.location.pathname && to.search === window.location.search) return;
      if (!confirmLeave()) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );
}

if (typeof window !== "undefined") {
  window.addEventListener(
    "popstate",
    (e) => {
      if (restoring) {
        restoring = false;
        e.stopImmediatePropagation();
        return;
      }
      if (!message || confirmLeave()) return; // nothing to lose, or they agreed: the router moves on
      // They chose to stay: the router never hears of it, and the address bar goes back.
      e.stopImmediatePropagation();
      const delta = stepBack(guardIdx, historyIndex(e.state));
      if (delta !== 0) {
        restoring = true;
        window.setTimeout(() => { restoring = false; }, 1000); // in case the step fires no popstate
        window.history.go(delta);
      }
    },
    true,
  );
}
